
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProfileStats } from "@/components/profile/ProfileStats";
import { BadgesList } from "@/components/profile/BadgesList";

interface ProfileDialogProps {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProfileDialog({ userId, open, onOpenChange }: ProfileDialogProps) {
  const { data: profile } = useQuery({
    queryKey: ['profile', userId],
    queryFn: async () => {
      if (!userId) return null;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const { data: stats = {
    total_floor_plans: 0,
    total_shared_floor_plans: 0,
    total_favorites: 0,
    average_rating: 0,
  }} = useQuery({
    queryKey: ['trainer-stats', userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from("trainer_statistics")
        .select("*")
        .eq("user_id", userId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const { data: badges = [] } = useQuery({
    queryKey: ['badges', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from("user_badges")
        .select(`
          id,
          badges (
            name,
            description
          ),
          awarded_at
        `)
        .eq("user_id", userId);

      if (error) throw error;
      return data.map((item: any) => ({
        id: item.id,
        name: item.badges.name,
        description: item.badges.description,
        awarded_at: item.awarded_at,
      }));
    },
    enabled: !!userId,
  });

  const displayName = profile?.display_name || profile?.username || "Naamloos";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Profiel</DialogTitle>
        </DialogHeader>
        {profile && (
          <div className="space-y-6">
            <div className="flex gap-4 items-center">
              <Avatar className="h-20 w-20">
                <AvatarImage src={profile.avatar_url || undefined} />
                <AvatarFallback>{displayName[0].toUpperCase()}</AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-2xl font-bold">{displayName}</h2>
              </div>
            </div>

            <ProfileStats stats={stats} />
            
            <BadgesList badges={badges} />

            <Card>
              <CardContent className="grid gap-4 pt-6">
                {profile.disciplines && (
                  <div>
                    <h3 className="font-semibold mb-1">Disciplines</h3>
                    <p className="text-muted-foreground">{profile.disciplines}</p>
                  </div>
                )}
                
                {profile.organisatie && (
                  <div>
                    <h3 className="font-semibold mb-1">Organisatie</h3>
                    <p className="text-muted-foreground">{profile.organisatie}</p>
                  </div>
                )}
                
                {profile.years_experience !== null && (
                  <div>
                    <h3 className="font-semibold mb-1">Jaren ervaring</h3>
                    <p className="text-muted-foreground">{profile.years_experience}</p>
                  </div>
                )}
                
                {profile.bio && (
                  <div>
                    <h3 className="font-semibold mb-1">Bio</h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">{profile.bio}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
